import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  DokaButton,
  DokaIcon,
  DokaModalController,
  DokaSearchField,
  DokaTable,
  DokaTbody,
  DokaTd,
  DokaTh,
  DokaThead,
  DokaTr,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { CurrenciesClient, ICountryModel, ICurrencyModel, LocationsClient } from 'src/api/services/generated/api-clients';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { CreateCurrencySlideOverComponent } from './slide-overs/create-currency-slide-over.component';
import { EditCurrencyCountriesSlideOverComponent } from './slide-overs/edit-currency-countries-slide-over.component';

@Component({
  selector: 'app-master-data-currencies',
  templateUrl: './master-data-currencies.component.html',
  imports: [
    DokaButton,
    DokaIcon,
    DokaSearchField,
    DokaTable,
    DokaTbody,
    DokaTd,
    DokaTh,
    DokaThead,
    DokaTr,
    I18NextEagerPipe,
    SpinnerComponent,
  ],
})
export class MasterDataCurrenciesComponent {
  private readonly currenciesClient = inject(CurrenciesClient);
  private readonly locationsClient = inject(LocationsClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);

  protected readonly searchText = signal('');
  protected readonly maxVisibleCountries = 5;

  protected readonly currencies = rxResource({
    stream: () => this.currenciesClient.getCurrencies(),
  });

  protected readonly countries = rxResource({
    stream: () => this.locationsClient.getCountries(undefined),
  });

  protected readonly filteredCurrencies = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    return (this.currencies.value()?.data ?? []).filter((currency) => {
      const countryNames = this.assignedCountries(currency.isoCurrencyCode)
        .map((country) => `${country.countryName} ${country.countryCode}`)
        .join(' ')
        .toLowerCase();

      return (
        currency.isoCurrencyCode.toLowerCase().includes(search) ||
        currency.currencySymbol.toLowerCase().includes(search) ||
        countryNames.includes(search)
      );
    });
  });

  protected assignedCountries(isoCurrencyCode: string): ICountryModel[] {
    return (this.countries.value()?.data ?? [])
      .filter((country) => country.isoCurrencyCodes.includes(isoCurrencyCode))
      .sort((left, right) => left.countryName.localeCompare(right.countryName));
  }

  protected async createCurrency() {
    const modal = await this.modalCtrl.create({ component: CreateCurrencySlideOverComponent }, 'slide-over');
    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (role === 'created' && data) {
      this.currencies.reload();
      await this.toastService.showSuccess(this.i18n.t('MasterData.Currencies.SuccessCreate'));
    }
  }

  protected async editCountries(currency: ICurrencyModel) {
    const modal = await this.modalCtrl.create(
      {
        component: EditCurrencyCountriesSlideOverComponent,
        componentProps: {
          currency: signal(currency).asReadonly(),
          countries: signal(this.countries.value()?.data ?? []).asReadonly(),
        },
      },
      'slide-over',
    );
    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (role === 'saved' && data) {
      this.countries.reload();
      await this.toastService.showSuccess(this.i18n.t('MasterData.Currencies.SuccessAssignment'));
    }
  }
}
